import { useState, useMemo, useEffect, useCallback } from 'react';
import { RotateCcw, Check, X } from 'lucide-react';
import type { MatchPairItem } from '../../services/mockQuizService';
import type { QuizProgress } from '../QuizBlock';

interface MatchingAnswers {
  matches: Record<number, number>;
  validatedMatches: Record<number, boolean>;
  shuffleOrder: number[]; // Store the shuffle order to restore state
}

interface MatchingPreviewProps {
  items: MatchPairItem[];
  initialAnswers?: MatchingAnswers;
  onAnswersChange?: (answers: MatchingAnswers) => void;
  onProgressChange?: (progress: QuizProgress) => void;
}

export const MatchingPreview = ({ items, initialAnswers, onAnswersChange, onProgressChange }: MatchingPreviewProps) => {
  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [selectedRight, setSelectedRight] = useState<number | null>(null);
  const [matches, setMatches] = useState<Record<number, number>>(initialAnswers?.matches || {});
  const [validatedMatches, setValidatedMatches] = useState<Record<number, boolean>>(initialAnswers?.validatedMatches || {});

  const leftItems = useMemo(() => items.map(p => p.left), [items]);
  
  // Shuffle right items - use stored order if available
  const shuffledRight = useMemo(() => {
    const arr = items.map((p, i) => ({ text: p.right, originalIndex: i }));
    
    if (initialAnswers?.shuffleOrder) {
      // Restore saved shuffle order
      return initialAnswers.shuffleOrder.map(idx => arr[idx]);
    }
    
    // Generate new shuffle
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [items, initialAnswers?.shuffleOrder]);

  // Get current shuffle order for saving
  const currentShuffleOrder = useMemo(() => {
    return shuffledRight.map(item => item.originalIndex);
  }, [shuffledRight]);

  // Calculate and report progress
  const updateProgress = useCallback(() => {
    const completed = Object.keys(matches).length;
    const correct = Object.values(validatedMatches).filter(v => v).length;
    onProgressChange?.({ total: items.length, completed, correct });
  }, [matches, validatedMatches, items.length, onProgressChange]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  const emitAnswersChange = useCallback((newMatches: Record<number, number>, newValidated: Record<number, boolean>) => {
    onAnswersChange?.({
      matches: newMatches,
      validatedMatches: newValidated,
      shuffleOrder: currentShuffleOrder,
    });
  }, [onAnswersChange, currentShuffleOrder]);

  const handleLeftClick = (index: number) => {
    if (matches[index] !== undefined) return;
    setSelectedLeft(index);
    if (selectedRight !== null) {
      const isCorrect = shuffledRight[selectedRight].originalIndex === index;
      const newMatches = { ...matches, [index]: selectedRight };
      const newValidated = { ...validatedMatches, [index]: isCorrect };
      setMatches(newMatches);
      setValidatedMatches(newValidated);
      setSelectedLeft(null);
      setSelectedRight(null);
      emitAnswersChange(newMatches, newValidated);
    }
  };

  const handleRightClick = (index: number) => {
    if (Object.values(matches).includes(index)) return;
    setSelectedRight(index);
    if (selectedLeft !== null) {
      const isCorrect = shuffledRight[index].originalIndex === selectedLeft;
      const newMatches = { ...matches, [selectedLeft]: index };
      const newValidated = { ...validatedMatches, [selectedLeft]: isCorrect };
      setMatches(newMatches);
      setValidatedMatches(newValidated);
      setSelectedLeft(null);
      setSelectedRight(null);
      emitAnswersChange(newMatches, newValidated);
    }
  };

  const handleReset = () => {
    setMatches({});
    setValidatedMatches({});
    setSelectedLeft(null);
    setSelectedRight(null);
    emitAnswersChange({}, {});
  };

  const allMatched = Object.keys(matches).length === leftItems.length;
  const allCorrect = allMatched && Object.values(validatedMatches).every(v => v);

  return (
    <div className="py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">
          Click pairs to match ({Object.keys(matches).length}/{leftItems.length} matched)
        </span>
        {Object.keys(matches).length > 0 && (
          <button
            onClick={handleReset}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Matching columns */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left column */}
        <div className="space-y-2">
          {leftItems.map((item, index) => {
            const isMatched = matches[index] !== undefined;
            const isSelected = selectedLeft === index;
            const isCorrect = validatedMatches[index];

            let classes = 'bg-white border-slate-200 hover:bg-slate-50';
            if (isMatched) {
              classes = isCorrect 
                ? 'bg-emerald-50 border-emerald-300' 
                : 'bg-red-50 border-red-300';
            } else if (isSelected) {
              classes = 'bg-slate-100 border-slate-400';
            }

            return (
              <button
                key={index}
                onClick={() => handleLeftClick(index)}
                disabled={isMatched}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors flex items-start gap-2 ${classes} ${isMatched ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <span className="flex-1">{item}</span>
                {isMatched && isCorrect && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />}
                {isMatched && !isCorrect && <X className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />}
              </button>
            );
          })}
        </div>

        {/* Right column */}
        <div className="space-y-2">
          {shuffledRight.map((item, index) => {
            const isMatched = Object.values(matches).includes(index);
            const isSelected = selectedRight === index;
            const matchedLeftIdx = Object.entries(matches).find(([_, r]) => r === index)?.[0];
            const isCorrect = matchedLeftIdx !== undefined && validatedMatches[Number(matchedLeftIdx)];

            let classes = 'bg-white border-slate-200 hover:bg-slate-50';
            if (isMatched) {
              classes = isCorrect 
                ? 'bg-emerald-50 border-emerald-300' 
                : 'bg-red-50 border-red-300';
            } else if (isSelected) {
              classes = 'bg-slate-100 border-slate-400';
            }

            return (
              <button
                key={index}
                onClick={() => handleRightClick(index)}
                disabled={isMatched}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${classes} ${isMatched ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <span>{item.text}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Result */}
      {allMatched && (
        <div className={`mt-4 p-3 rounded-lg text-sm text-center ${allCorrect ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          {allCorrect ? (
            <p className="font-medium text-emerald-700">✓ Perfect! All pairs matched correctly.</p>
          ) : (
            <p className="font-medium text-amber-700">Some pairs are incorrect. Check your answers above.</p>
          )}
        </div>
      )}
    </div>
  );
};
